import { Form, Formik } from 'formik';
import { type FC } from 'react';

import { OrderType } from '@/api/order/models/OrderType';
import { useCurrentUserInfo } from '@/auth/hooks/useCurrentUserInfo';
import { useHasPermission } from '@/components/Permissions/useHasPermission';
import { useOrderSteps } from '@/pages/Order/components/OrderStepsProvider';
import { useOrderPageContext } from '@/pages/Order/OrderPageContext';
import { WithdrawalOrder } from '@/pages/Order/types/Order';

import { useDiscretionaryPortfolios } from '../../hooks/useDiscretionaryPortfolios';
import { WithdrawalOrderFormValues } from '../../types/WithdrawalOrderFormValues';
import { getDefaultSignatoryOption } from '../../utils/getDefaultSignatoryOption';
import { getSignatoryOptionFromSignatories } from '../../utils/getSignatoryOptionFromSignatories';
import { useCreateWithdrawalValidationSchema } from '../../validationSchemas/hooks/useCreateWithdrawalValidationSchema';
import { WithdrawalOrderFormContent } from './WithdrawalOrderFormContent';

export const WithdrawalOrderForm: FC = () => {
  const hasPermission = useHasPermission();

  const { userType, userCmId, signature } = useCurrentUserInfo();

  const { completeCurrentStep } = useOrderSteps();
  const { setCurrentOrder, currentOrder } =
    useOrderPageContext<WithdrawalOrder>();

  const validationSchema = useCreateWithdrawalValidationSchema({
    userType,
  });

  const portfolios = useDiscretionaryPortfolios();

  if (portfolios.length === 0) {
    throw new Error(
      'Customer does not have any discretionary portfolios. One or more discretionary portfolios is required to use the withdrawal order page.',
    );
  }

  let initialPortfolioId = '';
  let initialBankAccountNumber = '';
  let initialCurrency = undefined;

  //?: Does the customer only have one portfolio?
  if (portfolios.length === 1) {
    // -> Yes, the customer has only one portfolio.

    // This means we can preselect the portfolio since
    // there is only one to choose from.
    initialPortfolioId = portfolios[0].id;

    const portfolioBankAccounts = portfolios[0].portfolioBankAccounts;

    // See if we can also preselect the portfolio bank account
    // if there is only one bank account to choose from.
    initialBankAccountNumber =
      portfolioBankAccounts.length === 1
        ? portfolioBankAccounts[0].accountNumber
        : '';

    // If there is only one portfolio bank account we can
    // then also set the currency.
    initialCurrency =
      portfolioBankAccounts.length === 1
        ? portfolioBankAccounts[0].currency
        : undefined;
  }

  let initialValues: WithdrawalOrderFormValues;
  if (currentOrder) {
    // The user has clicked the back button on the order summary page.
    // Populate the form with the previous values.
    initialValues = {
      portfolioId: currentOrder.portfolioId,
      fromPortfolioBankAccount: currentOrder.fromPortfolioBankAccountNumber,
      currency: currentOrder.currency,
      amount: currentOrder.amount,
      expectedTransferDate: currentOrder.expectedTransferDate,
      messageToSam: currentOrder.messageToSam ?? '',
      externalBankAccount: currentOrder.externalBankAccount,
      signer: currentOrder.signatories[0] ?? null,
      cosigner: currentOrder.signatories[1],
      signatoryOption: getSignatoryOptionFromSignatories(
        currentOrder.signatories,
        userCmId || '-1',
        hasPermission,
      ),
      signingProvider: currentOrder.signingProvider,
    };
  } else {
    // This is a fresh new order.
    // Initialize the order to its initial state.
    initialValues = {
      portfolioId: initialPortfolioId,
      fromPortfolioBankAccount: initialBankAccountNumber,
      currency: initialCurrency,
      amount: 0,
      expectedTransferDate: undefined,
      messageToSam: '',
      externalBankAccount: '',
      signer: null,
      cosigner: undefined,
      signatoryOption: getDefaultSignatoryOption(hasPermission),
      signingProvider: undefined,
    };
  }

  const handleSubmit = (values: WithdrawalOrderFormValues): void => {
    const order: WithdrawalOrder = {
      orderType: OrderType.Withdrawal,
      userId: userCmId || signature!,
      portfolioId: values.portfolioId,
      fromPortfolioBankAccountNumber: values.fromPortfolioBankAccount,
      amount: values.amount,
      currency: values.currency!,
      expectedTransferDate: values.expectedTransferDate!,
      externalBankAccount: values.externalBankAccount,
      messageToSam: values.messageToSam,
      signatories: [values.signer, values.cosigner].filter(
        (value) => value !== null && value !== undefined,
      ),
      signingProvider: values.signingProvider,
    };

    setCurrentOrder(order);
    completeCurrentStep();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <Form data-test="withdrawal-order-form">
        <WithdrawalOrderFormContent />
      </Form>
    </Formik>
  );
};
